import { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import { formatUTCDate } from 'helpers/DateHelpers';
import { isEmpty } from 'lodash';
import { tryCatch } from 'helpers/tryCatch';
import {
  useCreateCampaignMutation,
  useGetInsertionOrderQuery,
  useUpdateCampaignMutation,
} from 'reduxState/store/campaigns/api';
import {
  defaultCampaignFormData,
  displayTypeOptions,
  frequencyTypeOptions,
  insertionTypeOptions,
  defaultHTMLPopUpTemplateFormData,
} from '../constants';
import { validateCampaignUrlPatterns, isCampaignDestinationUrlValid } from '../utils';
import { ICampaignFormData, IHTMLPopUpTemplateFormData } from '../types';
import {
  ICampaign,
  ICampaignImage,
  ICampaignPayload,
  IUrlPattern,
  TInsertionNetworkMerchant,
} from 'reduxState/store/campaigns/types';
import { Application } from 'reduxState/store/application/types';
import { IDropdownSelectOption } from 'components/DropdownSearchSelect/DropdownSearchSelect';
import { buildHTMLPopUpTemplateFormData, buildHTMLPopUpTemplatePayload } from './utils/utils';

// Sync urlPatterns (string[]) and urlPatterns2 (IUrlPattern[]) for consistency
const syncUrlPatterns = (
  urlPatterns: string[] = [],
  urlPatterns2: IUrlPattern[] = [],
): { syncedUrlPatterns: string[]; syncedUrlPatterns2: IUrlPattern[] } => {
  let syncedUrlPatterns = urlPatterns || [];
  let syncedUrlPatterns2 = urlPatterns2 || [];

  // If urlPatterns has values but urlPatterns2 doesn't, convert urlPatterns to urlPatterns2
  if (syncedUrlPatterns.length > 0 && syncedUrlPatterns2.length === 0) {
    syncedUrlPatterns2 = syncedUrlPatterns.map(pattern => ({
      pattern,
      contentSelector: '',
      contentRegex: '',
      destinationURL: '',
    }));
  }
  // If urlPatterns2 has values but urlPatterns doesn't, extract patterns from urlPatterns2
  else if (syncedUrlPatterns2.length > 0 && syncedUrlPatterns.length === 0) {
    syncedUrlPatterns = syncedUrlPatterns2.map(urlPattern => urlPattern.pattern);
  } else if (syncedUrlPatterns2.length > 0 && syncedUrlPatterns.length > 0) {
    syncedUrlPatterns = syncedUrlPatterns2.map(urlPattern => urlPattern.pattern);
  }

  return { syncedUrlPatterns, syncedUrlPatterns2 };
};

const NEW_CAMPAIGN_DRAFT_STORAGE_KEY = 'partner-admin:new-campaign-draft';

/** Draft shape stored in localStorage (dates as ISO strings). */
interface INewCampaignDraft {
  formData: Record<string, unknown>;
  uploadedImages: ICampaignImage[];
  htmlPopUpTemplateFormData?: IHTMLPopUpTemplateFormData;
  selectedApps: Application[];
}

function hydrateFormData(raw: Record<string, unknown>): ICampaignFormData {
  const data = ({ ...raw } as unknown) as ICampaignFormData;
  if (raw.startDate && typeof raw.startDate === 'string') {
    data.startDate = new Date(raw.startDate);
  } else if (!raw.startDate) {
    data.startDate = null;
  }
  if (raw.endDate && typeof raw.endDate === 'string') {
    data.endDate = new Date(raw.endDate);
  } else if (!raw.endDate) {
    data.endDate = null;
  }
  return data;
}

function loadDraft(): INewCampaignDraft | null {
  try {
    const raw = localStorage.getItem(NEW_CAMPAIGN_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as INewCampaignDraft;
    if (!parsed?.formData) return null;
    return parsed;
  } catch (err) {
    console.warn('Failed to load campaign draft from localStorage:', err);
    clearNewCampaignDraft();
    return null;
  }
}

export function clearNewCampaignDraft(): void {
  try {
    localStorage.removeItem(NEW_CAMPAIGN_DRAFT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Serializable snapshot of form data for dirty checking (dates and options normalized). */
function formDataSnapshot(d: ICampaignFormData): string {
  return JSON.stringify({
    ...d,
    startDate: d.startDate?.getTime() ?? null,
    endDate: d.endDate?.getTime() ?? null,
    displayType: d.displayType?.value ?? null,
    frequencyType: d.frequencyType?.value ?? null,
    insertion: d.insertion?.value ?? null,
  });
}

interface UseCampaignFormProps {
  mode: 'create' | 'edit';
  selectedApps: Application[];
  setHasSelectedAppsError: (hasError: boolean) => void;
  resetSelectedApps: () => void;
  handleCloseModal: () => void;
  /** When a draft is restored from localStorage, called with the saved selectedApps so the parent can restore them. */
  onDraftRestored?: (selectedApps: Application[]) => void;
  /** When true (create mode), draft restore runs when this flips to true so reopening the modal after e.g. re-login restores the draft. */
  isCreateModalOpen?: boolean;
  // Create mode specific props
  campaignToDuplicate?: ICampaign | null;
  setCampaignToDuplicate?: (campaign: ICampaign | null) => void;
  // Edit mode specific props
  campaignToEdit?: ICampaign;
  setSelectedCampaignToEdit?: (campaign: ICampaign | null) => void;
}

export const useCampaignForm = ({
  mode,
  selectedApps,
  setHasSelectedAppsError,
  resetSelectedApps,
  handleCloseModal,
  onDraftRestored,
  isCreateModalOpen,
  campaignToDuplicate,
  setCampaignToDuplicate,
  campaignToEdit,
  setSelectedCampaignToEdit,
}: UseCampaignFormProps) => {
  const { user } = useAuth0();

  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState<ICampaignFormData>(defaultCampaignFormData);
  const [htmlPopUpTemplateFormData, setHTMLPopUpTemplateFormData] = useState<IHTMLPopUpTemplateFormData>(
    defaultHTMLPopUpTemplateFormData,
  );

  const [uploadedImages, setUploadedImages] = useState<ICampaignImage[]>([]);
  const [networkMerchant, setNetworkMerchant] = useState<TInsertionNetworkMerchant | null>(null);
  const [urlPatternsError, setUrlPatternsError] = useState<string>('');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState<boolean>(false);
  const [shouldUpdateStartDateToNow, setShouldUpdateStartDateToNow] = useState<boolean>(false);
  const [insertionOrderError, setInsertionOrderError] = useState<string>('');
  const skipNextTitleAutoGenerationRef = useRef(false);
  /** For edit/duplicate: snapshot of form when loaded from campaign; used to detect unsaved changes. */
  const initialFormSnapshotRef = useRef<string>(formDataSnapshot(defaultCampaignFormData));

  const { data: insertionOrderData, isError: isInsertionOrderError } = useGetInsertionOrderQuery(
    {
      id: Number(formData.insertionOrderId),
    },
    {
      skip: !formData.insertionOrderId || !Number(formData.insertionOrderId),
    },
  );

  const [createCampaignCallback, { isLoading: isCreatingCampaign }] = useCreateCampaignMutation();
  const [updateCampaignCallback, { isLoading: isUpdatingCampaign }] = useUpdateCampaignMutation();

  const isLoading = mode === 'create' ? isCreatingCampaign : isUpdatingCampaign;

  // useEffect to populate form data from campaign (either for duplication or editing)
  useEffect(() => {
    const campaign = mode === 'create' ? campaignToDuplicate : campaignToEdit;
    if (!campaign) return;

    const { config, title, merchantID, images, startDate, endDate, insertionOrderID, insertionOrderValue } = campaign;
    const {
      displayType,
      frequencyCap,
      urlPatterns,
      urlPatterns2,
      searchPhrases,
      popUp,
      inline,
      htmlPopUp,
      serpTagline,
      html,
      cta,
      headline,
      mainText,
      htmlPopUpTemplate,
      alertHTML,
    } = config;

    const destinationURL =
      popUp?.destinationURL ||
      htmlPopUp?.destinationURL ||
      inline?.destinationURL ||
      htmlPopUpTemplate?.destinationURL ||
      cta?.url ||
      '';
    const htmlInsertion = html?.insertion || inline?.insertion;

    // Handle dates for both edit and create (duplication)
    const formStartDate = startDate ? new Date(startDate) : null;
    const formEndDate = endDate ? new Date(endDate) : null;

    const { syncedUrlPatterns, syncedUrlPatterns2 } = syncUrlPatterns(urlPatterns, urlPatterns2);

    const newFormData: ICampaignFormData = {
      title,
      merchantId: merchantID?.toString() || '',
      merchantDisplayName: '',
      networkMerchantId: '',
      headline: headline || popUp?.headline || '',
      startDate: formStartDate,
      endDate: formEndDate,
      urlPatterns: syncedUrlPatterns,
      urlPatterns2: syncedUrlPatterns2,
      mainText: mainText || popUp?.mainText || '',
      ctaColor: cta?.color || popUp?.ctaColor || '',
      ctaLabel: cta?.label || popUp?.ctaLabel || '',
      destinationURL,
      target: inline?.target || html?.target || html?.target || '',
      insertion: insertionTypeOptions.find(option => option.value === htmlInsertion) || null,
      style: inline?.style || htmlPopUp?.style || html?.style || alertHTML?.style || '',
      content: inline?.content || htmlPopUp?.content || html?.content || alertHTML?.content || '',
      alertTypes: alertHTML?.alertTypes || [],
      googleSheetID: campaign.googleSheetID || '',
      displayType: displayTypeOptions.find(option => option.value === displayType) || null,
      frequencyType: frequencyTypeOptions.find(option => option.value === frequencyCap?.type) || null,
      minutes: frequencyCap?.minutes,
      limit: frequencyCap?.limit,
      cooldown: frequencyCap?.cooldown,
      serpTagline,
      searchPhrases,
      insertionOrderId: insertionOrderID?.toString() || '',
      insertionOrderValue: insertionOrderValue || '',
    };

    setFormData(newFormData);
    initialFormSnapshotRef.current = formDataSnapshot(newFormData);

    if (htmlPopUpTemplate) {
      const copiedHTMLPopUpTemplateFormData = buildHTMLPopUpTemplateFormData(htmlPopUpTemplate);
      setHTMLPopUpTemplateFormData(copiedHTMLPopUpTemplateFormData);
    }

    if (images && images.length > 0) {
      setUploadedImages(images);
    }
  }, [mode, campaignToDuplicate, campaignToEdit]);

  // Restore draft from localStorage when opening create form (no duplicate)
  useEffect(() => {
    if (mode !== 'create' || campaignToDuplicate) return;
    if (isCreateModalOpen !== true) return; // only restore when the create modal is explicitly open
    const draft = loadDraft();
    if (!draft) return;
    try {
      const hydratedFormData = hydrateFormData(draft.formData as Record<string, unknown>);
      setFormData(hydratedFormData);
      if (draft.uploadedImages?.length) {
        setUploadedImages(draft.uploadedImages);
      }
      if (draft.htmlPopUpTemplateFormData) {
        setHTMLPopUpTemplateFormData(draft.htmlPopUpTemplateFormData);
      }
      if (draft.selectedApps?.length && onDraftRestored) {
        onDraftRestored(draft.selectedApps);
      }
    } catch (err) {
      console.warn('Failed to restore campaign draft:', err);
      clearNewCampaignDraft();
    }
  }, [mode, campaignToDuplicate, isCreateModalOpen, onDraftRestored]);

  // Persist create-form draft to localStorage (debounced)
  useEffect(() => {
    if (mode !== 'create' || campaignToDuplicate) return;
    const timeout = setTimeout(() => {
      const draft: INewCampaignDraft = {
        formData: (formData as unknown) as Record<string, unknown>,
        uploadedImages,
        htmlPopUpTemplateFormData,
        selectedApps,
      };
      try {
        localStorage.setItem(NEW_CAMPAIGN_DRAFT_STORAGE_KEY, JSON.stringify(draft));
      } catch {
        // ignore quota / private mode
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [mode, campaignToDuplicate, formData, uploadedImages, htmlPopUpTemplateFormData, selectedApps]);

  useEffect(() => {
    if (insertionOrderData) {
      setNetworkMerchant({
        id: insertionOrderData.networkMerchantID,
        name: insertionOrderData.networkMerchantName,
      });
      setInsertionOrderError('');
    }
  }, [insertionOrderData]);

  // Surface insertion order lookup errors to the user
  useEffect(() => {
    if (isInsertionOrderError && formData.insertionOrderId) {
      setNetworkMerchant(null);
      setInsertionOrderError(
        `Insertion Order ID "${formData.insertionOrderId}" not found. Please create this Insertion Order in the system first, or enter a different IO ID that exists.`,
      );
    } else if (!formData.insertionOrderId) {
      setInsertionOrderError('');
    }
  }, [isInsertionOrderError, formData.insertionOrderId]);

  const handleInputChange = (value: string, field: keyof ICampaignFormData) => {
    if (value) {
      setFormErrors(prev => ({ ...prev, [field]: false }));
    }
    if (field === 'title' && value === '') {
      skipNextTitleAutoGenerationRef.current = true;
    }
    if (field === 'insertionOrderId') {
      setInsertionOrderError('');
    }
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'merchantId' && !value) {
        next.merchantDisplayName = '';
      }
      return next;
    });
  };

  const handleNumberInputChange = (value: string, field: keyof ICampaignFormData) => {
    const parsedValue = parseInt(value);
    if (!isNaN(parsedValue) && parsedValue >= 0) {
      setFormErrors(prev => ({ ...prev, [field]: false }));
    }
    setFormData(prev => ({
      ...prev,
      [field]: parsedValue,
    }));
  };

  const handleAddUrlPattern = (value: IUrlPattern) => {
    if ((formErrors.searchPhrases || formErrors.urlPatterns || formErrors.urlPatterns2) && value.pattern) {
      setFormErrors(prev => ({
        ...prev,
        urlPatterns2: false,
        urlPatterns: false,
        searchPhrases: false,
      }));
    }
    if (value) {
      setFormData(prev => ({
        ...prev,
        urlPatterns: [...prev.urlPatterns, value.pattern],
        urlPatterns2: [...prev.urlPatterns2, value],
      }));
    }
  };

  const handleRemoveUrlPattern = (index: number) => {
    setFormData(prev => ({
      ...prev,
      urlPatterns: prev.urlPatterns.filter((_, i) => i !== index),
      urlPatterns2: prev.urlPatterns2.filter((_, i) => i !== index),
    }));
  };

  const handleUpdateUrlPattern = (index: number, value: IUrlPattern) => {
    setFormData(prev => ({
      ...prev,
      urlPatterns2: prev.urlPatterns2.map((urlPattern, i) => (i === index ? value : urlPattern)),
    }));
  };

  const handleAddSearchPhrase = (searchPhrase: string) => {
    if ((formErrors.searchPhrases || formErrors.urlPatterns || formErrors.urlPatterns2) && searchPhrase.trim()) {
      setFormErrors(prev => ({
        ...prev,
        searchPhrases: false,
        urlPatterns: false,
        urlPatterns2: false,
      }));
    }
    if (searchPhrase.trim()) {
      setFormData(prev => ({
        ...prev,
        searchPhrases: [...prev.searchPhrases, searchPhrase.trim()],
      }));
    }
  };

  const handleRemoveSearchPhrase = (index: number) => {
    setFormData(prev => ({
      ...prev,
      searchPhrases: prev.searchPhrases.filter((_, i) => i !== index),
    }));
  };

  const handleSetUploadedImages = (images: ICampaignImage[]) => {
    setUploadedImages(images);
  };

  const handleDisplayTypeChange = (option: IDropdownSelectOption | null) => {
    if (option) {
      setFormErrors({ displayType: false });
    }

    switch (option?.value) {
      case 'POP_UP':
        setFormData(prev => ({
          ...prev,
          target: '',
          content: '',
          style: '',
          displayType: option,
        }));
        break;
      case 'INLINE':
      case 'HTML_POP_UP':
        setFormData(prev => ({
          ...prev,
          headline: '',
          mainText: '',
          ctaColor: '',
          ctaLabel: '',
          displayType: option,
        }));
        break;
      case 'ALERT_HTML':
        setFormData(prev => ({
          ...prev,
          displayType: option,
          headline: '',
          mainText: '',
          ctaColor: '',
          ctaLabel: '',
          target: '',
          content: '',
          style: '',
          alertTypes: [],
        }));
        break;
      case 'HTML_POP_UP_TEMPLATE':
      default:
        setFormData(prev => ({
          ...prev,
          displayType: option,
          headline: '',
          mainText: '',
          ctaColor: '',
          ctaLabel: '',
          target: '',
          content: '',
          style: '',
          alertTypes: [],
        }));
        break;
    }
  };

  const handleStartDateManualChange = (date: Date | null) => {
    setShouldUpdateStartDateToNow(false);
    setFormData(prev => ({ ...prev, startDate: date }));
    if (date) {
      setFormErrors(prev => ({ ...prev, startDate: false }));
    }
  };

  const handleStartDateNowClick = () => {
    setShouldUpdateStartDateToNow(true);
    setFormData(prev => ({ ...prev, startDate: new Date() }));
    setFormErrors(prev => ({ ...prev, startDate: false }));
  };

  const validateForm = () => {
    const {
      displayType,
      title,
      startDate,
      endDate,
      frequencyType,
      minutes,
      limit,
      cooldown,
      searchPhrases,
      urlPatterns,
      urlPatterns2,
      headline,
      mainText,
      ctaLabel,
      target,
      insertion,
      style,
      content,
      destinationURL,
      insertionOrderId,
      insertionOrderValue,
    } = formData;

    const newFormErrors = {} as Record<string, boolean>;

    if (selectedApps.length === 0) setHasSelectedAppsError(true);
    if (!title) newFormErrors.title = true;
    if (!displayType) newFormErrors.displayType = true;
    if (searchPhrases.length === 0 && urlPatterns.length === 0 && urlPatterns2.length === 0) {
      newFormErrors.searchPhrases = true;
      newFormErrors.urlPatterns = true;
      newFormErrors.urlPatterns2 = true;
    }
    if (!startDate || isNaN(startDate.getTime())) newFormErrors.startDate = true;
    if (!endDate || isNaN(endDate.getTime())) newFormErrors.endDate = true;
    if (!frequencyType) newFormErrors.frequencyType = true;
    if (minutes === null || minutes === undefined || !Number.isInteger(minutes)) newFormErrors.minutes = true;
    if (cooldown === null || cooldown === undefined || !Number.isInteger(cooldown)) newFormErrors.cooldown = true;
    if (limit === null || !Number.isInteger(limit)) newFormErrors.limit = true;
    if (!insertionOrderId) newFormErrors.insertionOrderId = true;
    if (!insertionOrderValue) newFormErrors.insertionOrderValue = true;

    // Show insertion order lookup error before generic required-fields message
    if (insertionOrderError) {
      setFormErrors(prev => ({ ...prev, insertionOrderId: true }));
      toast.error(insertionOrderError);
      return false;
    }

    // Bail early with toast if any of the base required fields are missing
    if (
      Object.keys(newFormErrors).length > 0 ||
      selectedApps.length === 0 ||
      !displayType ||
      !frequencyType ||
      !startDate ||
      !endDate ||
      minutes === null ||
      minutes === undefined ||
      cooldown === null ||
      cooldown === undefined ||
      limit === null
    ) {
      setFormErrors(newFormErrors);
      toast.error('Please fill out the required fields');
      return false;
    }

    if (limit === 0 && frequencyType?.value === 'VIEW_COUNT') {
      setFormErrors(prev => ({ ...prev, limit: true }));
      toast.error('Limit must be greater than 0 for View Count frequency type');
      return false;
    }

    // Form validation based on display type
    switch (displayType?.value) {
      case 'POP_UP': {
        if (!headline) newFormErrors.headline = true;
        if (!mainText) newFormErrors.mainText = true;
        if (!ctaLabel) newFormErrors.ctaLabel = true;
        if (!destinationURL) newFormErrors.destinationURL = true;
        if (Object.keys(newFormErrors).length > 0) {
          setFormErrors(newFormErrors);
          toast.error('Please fill out the required fields for a Pop Up campaign');
          return false;
        }
        break;
      }
      case 'INLINE':
      case 'HTML_POP_UP': {
        if (!target && displayType?.value === 'INLINE') newFormErrors.target = true;
        if (!insertion && displayType?.value === 'INLINE') newFormErrors.insertion = true;
        if (!content) newFormErrors.content = true;
        if (!style) newFormErrors.style = true;
        if (Object.keys(newFormErrors).length > 0) {
          setFormErrors(newFormErrors);
          toast.error('Please fill out the required fields for a Inline or HTML Pop Up campaign');
          return false;
        }
        break;
      }
      case 'ALERT_HTML': {
        if (formData.alertTypes.length === 0) newFormErrors.alertTypes = true;
        if (!content) newFormErrors.content = true;
        if (!style) newFormErrors.style = true;
        if (Object.keys(newFormErrors).length > 0) {
          setFormErrors(newFormErrors);
          toast.error('Please fill out the required fields for an Alert HTML campaign');
          return false;
        }
        break;
      }
    }

    const isUrlPatternsValid = validateCampaignUrlPatterns(urlPatterns2);
    const isDestinationUrlValid = isCampaignDestinationUrlValid(destinationURL);

    if (!isUrlPatternsValid || !isDestinationUrlValid) {
      setFormErrors(prev => ({
        ...prev,
        urlPatterns2: !isUrlPatternsValid,
        destinationURL: !isDestinationUrlValid,
      }));
      toast.error('Please enter valid URLs');
      return false;
    } else if (formErrors.urlPatterns || formErrors.destinationURL) {
      setFormErrors(prev => ({ ...prev, urlPatterns: !isUrlPatternsValid, destinationURL: !isDestinationUrlValid }));
      return false;
    }

    // Check for unsaved URL patterns
    if (!isEmpty(urlPatternsError)) {
      toast.error(urlPatternsError);
      return false;
    }

    return true;
  };

  const buildCampaignPayload = (startDate?: Date | null): ICampaignPayload => {
    const {
      displayType,
      title,
      merchantId,
      startDate: formStartDate,
      endDate,
      frequencyType,
      minutes,
      limit,
      cooldown,
      searchPhrases,
      urlPatterns,
      urlPatterns2,
      headline,
      mainText,
      ctaColor,
      ctaLabel,
      destinationURL,
      target,
      insertion,
      style,
      content,
      serpTagline,
      insertionOrderValue,
      insertionOrderId,
      alertTypes,
      googleSheetID,
    } = formData;

    const finalStartDate = startDate || formStartDate;

    // Handle merchant ID conversion
    const parsedMerchantID = merchantId ? parseInt(merchantId) : null;

    const payload: ICampaignPayload = {
      title,
      disabled: false,
      applicationIDs: selectedApps.map((app: Application) => parseInt(app.id)),
      merchantID: parsedMerchantID,
      startDate: formatUTCDate(finalStartDate!, "yyyy-MM-dd'T'HH:mm:ssXXX"),
      endDate: formatUTCDate(endDate!, "yyyy-MM-dd'T'HH:mm:ssXXX"),
      imageIDs: uploadedImages.map((image: ICampaignImage) => image.id),
      modifiedAuthor: user?.email || 'Unknown Wildfire user',
      insertionOrderValue: parseFloat(insertionOrderValue).toFixed(2),
      insertionOrderID: insertionOrderId ? parseInt(insertionOrderId) : null,
      networkMerchantID: networkMerchant?.id ? networkMerchant.id : null,
      googleSheetID: googleSheetID || null,
      config: {
        displayType: displayType!.value,
        frequencyCap: {
          type: frequencyType!.value,
          limit: limit!,
          minutes: minutes!,
          cooldown: cooldown!,
        },
        urlPatterns: urlPatterns,
        urlPatterns2: urlPatterns2,
        searchPhrases: searchPhrases,
        serpTagline: serpTagline,
      },
    };

    switch (displayType!.value) {
      case 'POP_UP':
        payload.config.popUp = {
          headline,
          mainText,
          ctaColor,
          destinationURL,
          ctaLabel,
        };
        break;
      case 'INLINE':
        payload.config.inline = {
          target,
          insertion: insertion?.value || null,
          content,
          style,
          destinationURL,
        };
        break;
      case 'HTML_POP_UP':
        payload.config.htmlPopUp = {
          style,
          content,
          destinationURL,
        };
        break;
      case 'ALERT_HTML':
        payload.config.alertHTML = {
          content,
          style,
          alertTypes,
        };
        if (destinationURL) {
          // color and label are not used by ALERT_HTML — only url is read by the extension.
          // Empty strings are accepted by the API since the cta type requires all three fields.
          payload.config.cta = { color: '', label: '', url: destinationURL };
        }
        break;
      case 'HTML_POP_UP_TEMPLATE':
        payload.config.htmlPopUpTemplate = buildHTMLPopUpTemplatePayload({
          ...htmlPopUpTemplateFormData,
          destinationURL,
        });
        break;
    }

    return payload;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);
    if (!validateForm()) return;

    // Update date if they were added via shortcut
    const currentDate = new Date();
    const finalStartDate = shouldUpdateStartDateToNow ? currentDate : formData.startDate;

    const payload = buildCampaignPayload(finalStartDate);

    setShouldUpdateStartDateToNow(false);

    let promise;
    if (mode === 'create') {
      promise = createCampaignCallback(payload).unwrap();
    } else {
      promise = updateCampaignCallback({
        id: campaignToEdit!.id.toString(),
        payload: payload,
      }).unwrap();
    }

    const { error } = await tryCatch(promise);
    if (error) {
      const actionText = mode === 'create' ? 'creating' : 'updating';
      toast.error(`Error occurred while ${actionText} campaign`);
      return;
    }

    const successText = mode === 'create' ? 'created' : 'updated';
    toast.success(`Campaign ${successText} successfully`);
    clearNewCampaignDraft();
    handleClearForm();
    handleCloseModal();
  };

  const handleClearForm = () => {
    clearNewCampaignDraft();
    skipNextTitleAutoGenerationRef.current = true;
    setFormData({ ...defaultCampaignFormData, title: '' });
    setUploadedImages([]);
    setFormErrors({});
    resetSelectedApps();
    setUrlPatternsError('');
    setHasAttemptedSubmit(false);
    setShouldUpdateStartDateToNow(false);

    if (mode === 'create' && campaignToDuplicate && setCampaignToDuplicate) {
      setCampaignToDuplicate(null);
    } else if (mode === 'edit' && setSelectedCampaignToEdit) {
      setSelectedCampaignToEdit(null);
    }
  };

  const handleCancel = () => {
    if (mode === 'edit' && setSelectedCampaignToEdit) {
      setSelectedCampaignToEdit(null);
    }
    handleCloseModal();
  };

  const hasFormChanges =
    mode === 'create' && !campaignToDuplicate
      ? selectedApps.length > 0 || formDataSnapshot(formData) !== formDataSnapshot(defaultCampaignFormData)
      : formDataSnapshot(formData) !== initialFormSnapshotRef.current;

  return {
    // State
    formData,
    setFormData,
    formErrors,
    setFormErrors,
    uploadedImages,
    isLoading,
    networkMerchant,
    setNetworkMerchant,
    urlPatternsError,
    insertionOrderError,
    htmlPopUpTemplateFormData,
    hasAttemptedSubmit,
    shouldUpdateStartDateToNow,
    setShouldUpdateStartDateToNow,

    // Handlers
    handleInputChange,
    handleNumberInputChange,
    handleAddUrlPattern,
    handleRemoveUrlPattern,
    handleAddSearchPhrase,
    handleRemoveSearchPhrase,
    handleUpdateUrlPattern,
    handleSetUploadedImages,
    handleDisplayTypeChange,
    handleStartDateManualChange,
    handleStartDateNowClick,
    handleSubmit,
    handleClearForm,
    handleCancel,
    setHTMLPopUpTemplateFormData,
    setUrlPatternsError,

    // Edit-specific data for conditional rendering
    campaignToEdit,
    skipNextTitleAutoGenerationRef,
    hasFormChanges,
  };
};
